/**
 * Fetch-Wrapper für `/api/*` mit Session-Cookie (browser-managed).
 * Wirft `ApiError` mit { status, message } für 4xx/5xx, oder einen
 * generischen `Error("Netzwerkfehler")` bei reinem fetch-fail (offline).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly retryAfterSeconds: number | null;
  /** Parsed JSON body of the error response (or null if non-JSON). Sprint C3a:
   *  Invite-Modal liest `body.deactivated` als 409-recovery-flag. */
  readonly body: Record<string, unknown> | null;

  constructor(
    message: string,
    status: number,
    retryAfterSeconds: number | null = null,
    body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
    this.body = body;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const num = parseInt(raw, 10);
  if (Number.isFinite(num) && num >= 0) return num;
  return null;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch (err) {
    // fetch() rejected nur bei DNS-Fail / offline / CORS / abort.
    // AbortError preservieren damit Caller (z.B. SearchSuggestPopover)
    // canceled in-flight requests silent verwerfen kann.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new Error('Netzwerkfehler', { cause: err });
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | Record<string, unknown>
      | null;
    const errorMessage =
      typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`;
    throw new ApiError(
      errorMessage,
      res.status,
      parseRetryAfter(res.headers.get('Retry-After')),
      body,
    );
  }
  return (await res.json()) as T;
}

/**
 * Fetch-Variante die bei Fehler `null` returnt statt zu werfen —
 * nützlich für optionale Checks wie GET /api/auth/me.
 */
export async function apiTryGet<T>(path: string): Promise<T | null> {
  try {
    return await api<T>(path);
  } catch {
    return null;
  }
}
